import { NgClass, NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit, forwardRef, inject } from '@angular/core';
import { Events } from '@df/core/service/event';
import { AdvancedCommerceListingComponentDef, AdvancedCommerceStore } from '@df/module-advanced-commerce';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { AfterContentInitDirective, AfterContentInitDirectiveService } from '@df/ui/common/after-content-init.directive';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { Subscription } from 'rxjs';
import { toggleTieredPriceModeListingEvent } from '../../../catalog/ui/listing/product-listing-tools.component';
import { NListing } from '../../../catalog/ui/listing/product-listing.interface';
import { ProductComponent } from '../../../catalog/ui/product/product.component';
import { GtmImpressionTrackDirective } from '../../gtm/gtm-impression-track.directive';
import { GtmTrackProductClickDirective } from '../../gtm/gtm-track-product-click.directive';
import { VipService } from '../../vip/vip.service';
import { AdvancedCommerceListingToolsComponent } from './advanced-commerce-product-listing-tools.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';

@Component({
    selector: 'ac-product-listing',
    templateUrl: '../../../catalog/ui/listing/product-listing.html',
    standalone: true,
    imports: [
        AfterContentInitDirective,
        GridModule,
        GtmImpressionTrackDirective,
        GtmTrackProductClickDirective,
        NgClass,
        ICmsClientModule,
        NgComponentOutlet,
        forwardRef(() => ProductComponent),
        TranslationModule
    ],
    styleUrls: ['../../../catalog/ui/listing/product-listing.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdvancedCommerceProductListingComponent extends AdvancedCommerceListingComponentDef implements OnInit, NListing.IListing {
    private subscriptions = new Subscription();
    canShowTieredPrice: boolean | undefined;
    protected afterContentInit = inject(AfterContentInitDirectiveService);
    private events = inject(Events);
    private vipService = inject(VipService);
    @Input()
    acClickCode?: string;

    private wasReady = false;

    @Input() searchTerm: string | undefined;
    override ngOnInit() {
        super.ngOnInit();
        this.subscriptions.add(
            this.afterContentInit.ready.subscribe(value => {
                if (value) {
                    this.changeDetectorRef.detectChanges();
                }
            })
        );
        this.subscriptions.add(this.events.on(toggleTieredPriceModeListingEvent).subscribe(event => this.onTieredPriceModeChange(event)));
    }

    onTieredPriceModeChange(value: boolean): void {
        this.canShowTieredPrice = value;
        this.changeDetectorRef.detectChanges();
    }

    get canShow(): boolean {
        if (!this.wasReady && this.ready) {
            this.wasReady = true;
        }
        return this.wasReady;
    }

    get ready(): boolean {
        return !this.store.isLoading() && !!this.store.response();
    }

    get models(): NListing.IModel[] {
        return this.store.itemsEntities() as NListing.IModel[];
    }

    get gridSize(): number {
        return this.store.listingCurrentSize();
    }

    colspan(model: any): number {
        return model.type === 'advert' ? model.size : 1;
    }

    rowspan(model: any): number {
        return 1;
    }

    get getTotal(): number {
        return this.vipService.adjustCountForRestrictions(this.store.metaCount());
    }

    get hasMore(): boolean {
        return this.store.hasMore();
    }

    get visibleQty(): number {
        return this.vipService.adjustCountForRestrictions(this.store.total());
    }

    increaseLimit(): void {
        this.store.loadMore();
    }

    get toolsOutlet(): NListing.IOutlet {
        return { component: AdvancedCommerceListingToolsComponent };
    }

    get context(): string | undefined {
        return this.element();
    }

    get clickCode(): string | undefined {
        return this.acClickCode || this.store.metaClickCode();
    }

    // need to make compilator happy (listing is needed for klevu tracking fo usual listing)
    get listing() {
        return undefined;
    }

    protected toProductId(model: NListing.IModel): number {
        const id = model.id;
        return typeof id === 'number' ? id : Number(id);
    }

    protected toAcItem(model: NListing.IModel): AdvancedCommerceStore.Items.Item | undefined {
        return model as AdvancedCommerceStore.Items.Item;
    }
}
